<template>
  <v-dialog
    v-model="show"
    fullscreen
    transition="dialog-bottom-transition"
    class="image-lightbox"
  >
    <v-card class="lightbox-card">
      <!-- Header with controls -->
      <v-toolbar color="black" class="lightbox-toolbar">
        <v-toolbar-title class="text-truncate">
          {{ currentTitle }}
          <span v-if="hasMultipleImages" class="ml-2 text-caption">
            ({{ currentIndex + 1 }} / {{ images.length }})
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Zoom controls -->
        <v-btn
          icon="mdi-minus"
          variant="text"
          color="white"
          :disabled="scale <= minScale"
          @click="zoomOut"
        ></v-btn>

        <v-chip class="mx-2" variant="flat" color="rgba(255,255,255,0.2)">
          {{ Math.round(scale * 100) }}%
        </v-chip>

        <v-btn
          icon="mdi-plus"
          variant="text"
          color="white"
          :disabled="scale >= maxScale"
          @click="zoomIn"
        ></v-btn>

        <v-btn
          icon="mdi-fit-to-screen"
          variant="text"
          color="white"
          @click="resetZoom"
        ></v-btn>

        <!-- Download button -->
        <v-btn
          v-if="currentDownloadUrl"
          icon="mdi-download"
          variant="text"
          color="white"
          :href="currentDownloadUrl"
          target="_blank"
        ></v-btn>

        <!-- Delete button -->
        <v-btn
          v-if="allowDelete"
          icon="mdi-delete"
          variant="text"
          color="error"
          @click="handleDelete"
        ></v-btn>

        <!-- Close button -->
        <v-btn
          icon="mdi-close"
          variant="text"
          color="white"
          @click="close"
        ></v-btn>
      </v-toolbar>

      <!-- Image container -->
      <v-card-text
        ref="containerRef"
        class="lightbox-container"
        @wheel.prevent="handleWheel"
        @mousedown="handleMouseDown"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @dblclick="handleDoubleClick"
      >
        <!-- Previous image button -->
        <v-btn
          v-if="canGoPrev"
          icon="mdi-chevron-left"
          size="x-large"
          variant="tonal"
          color="white"
          class="nav-btn nav-btn-left"
          @click="goToPrev"
        ></v-btn>

        <img
          ref="imageRef"
          :src="currentImageUrl"
          :style="imageStyle"
          class="lightbox-image"
          @load="onImageLoad"
        />

        <!-- Next image button -->
        <v-btn
          v-if="canGoNext"
          icon="mdi-chevron-right"
          size="x-large"
          variant="tonal"
          color="white"
          class="nav-btn nav-btn-right"
          @click="goToNext"
        ></v-btn>
      </v-card-text>

      <!-- Mobile zoom hint -->
      <div v-if="isMobile && scale === 1" class="zoom-hint">
        <v-icon size="small" color="white">mdi-information</v-icon>
        <span class="ml-2">Dubbeltik om te zoomen</span>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";

export default {
  name: "ImageLightbox",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      default: null,
    },
    downloadUrl: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    // Support for multiple images
    images: {
      type: Array,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    // Allow delete functionality
    allowDelete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "delete"],
  setup(props, { emit }) {
    const show = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    const containerRef = ref(null);
    const imageRef = ref(null);

    // Multiple images support
    const currentIndex = ref(props.initialIndex);
    const hasMultipleImages = computed(
      () => props.images && props.images.length > 1,
    );

    const currentImage = computed(() => {
      if (props.images && props.images.length > 0) {
        return props.images[currentIndex.value] || props.images[0];
      }
      return {
        file: props.imageUrl,
        file_dl: props.downloadUrl,
        name: props.title,
      };
    });

    const currentImageUrl = computed(
      () => currentImage.value?.file || props.imageUrl,
    );
    const currentDownloadUrl = computed(
      () => currentImage.value?.file_dl || props.downloadUrl,
    );
    const currentTitle = computed(
      () => currentImage.value?.name || props.title || "Afbeelding",
    );

    const canGoPrev = computed(
      () => hasMultipleImages.value && currentIndex.value > 0,
    );
    const canGoNext = computed(
      () =>
        hasMultipleImages.value && currentIndex.value < props.images.length - 1,
    );

    // Zoom state
    const scale = ref(1);
    const translateX = ref(0);
    const translateY = ref(0);
    const minScale = 1;
    const maxScale = 5;
    const zoomStep = 0.3;

    // Touch/pan state
    const isPanning = ref(false);
    const startX = ref(0);
    const startY = ref(0);

    // Touch zoom state
    const initialPinchDistance = ref(0);
    const initialScale = ref(1);

    // Device detection
    const isMobile = computed(() => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
    });

    const imageStyle = computed(() => ({
      transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
      cursor:
        scale.value > 1 ? (isPanning.value ? "grabbing" : "grab") : "default",
      transition: isPanning.value ? "none" : "transform 0.2s ease",
    }));

    // Navigation functions
    const goToPrev = () => {
      if (canGoPrev.value) {
        currentIndex.value--;
        resetZoom();
      }
    };

    const goToNext = () => {
      if (canGoNext.value) {
        currentIndex.value++;
        resetZoom();
      }
    };

    // Keyboard navigation
    const handleKeyDown = (event) => {
      if (!show.value) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNext();
      } else if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };

    // Reset zoom when closing or changing image
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          currentIndex.value = props.initialIndex;
          resetZoom();
        }
      },
    );

    watch(
      () => props.imageUrl,
      () => {
        resetZoom();
      },
    );

    watch(
      () => currentIndex.value,
      () => {
        resetZoom();
      },
    );

    const resetZoom = () => {
      scale.value = 1;
      translateX.value = 0;
      translateY.value = 0;
    };

    const zoomIn = () => {
      if (scale.value < maxScale) {
        scale.value = Math.min(scale.value + zoomStep, maxScale);
      }
    };

    const zoomOut = () => {
      if (scale.value > minScale) {
        scale.value = Math.max(scale.value - zoomStep, minScale);
        if (scale.value === minScale) {
          translateX.value = 0;
          translateY.value = 0;
        }
      }
    };

    const handleWheel = (event) => {
      if (event.ctrlKey || event.metaKey) {
        // Zoom with mouse wheel (Ctrl+scroll)
        const delta = -event.deltaY;
        if (delta > 0) {
          zoomIn();
        } else {
          zoomOut();
        }
      }
    };

    const handleDoubleClick = (event) => {
      if (scale.value === 1) {
        // Zoom to 2x at click position
        scale.value = 2;

        // Calculate position to zoom into clicked point
        const rect = containerRef.value.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        translateX.value = (centerX - x) * 0.5;
        translateY.value = (centerY - y) * 0.5;
      } else {
        resetZoom();
      }
    };

    // Mouse pan
    const handleMouseDown = (event) => {
      if (scale.value > 1) {
        isPanning.value = true;
        startX.value = event.clientX - translateX.value;
        startY.value = event.clientY - translateY.value;
        event.preventDefault();
      }
    };

    const handleMouseMove = (event) => {
      if (isPanning.value && scale.value > 1) {
        translateX.value = event.clientX - startX.value;
        translateY.value = event.clientY - startY.value;
      }
    };

    const handleMouseUp = () => {
      isPanning.value = false;
    };

    // Touch pan and pinch zoom
    const getTouchDistance = (touch1, touch2) => {
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (event) => {
      if (event.touches.length === 1 && scale.value > 1) {
        // Single touch pan
        isPanning.value = true;
        const touch = event.touches[0];
        startX.value = touch.clientX - translateX.value;
        startY.value = touch.clientY - translateY.value;
      } else if (event.touches.length === 2) {
        // Two finger pinch zoom
        isPanning.value = false;
        initialPinchDistance.value = getTouchDistance(
          event.touches[0],
          event.touches[1],
        );
        initialScale.value = scale.value;
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 1 && isPanning.value && scale.value > 1) {
        // Pan
        const touch = event.touches[0];
        translateX.value = touch.clientX - startX.value;
        translateY.value = touch.clientY - startY.value;
        event.preventDefault();
      } else if (event.touches.length === 2) {
        // Pinch zoom
        const currentDistance = getTouchDistance(
          event.touches[0],
          event.touches[1],
        );
        const scaleChange = currentDistance / initialPinchDistance.value;
        scale.value = Math.min(
          Math.max(initialScale.value * scaleChange, minScale),
          maxScale,
        );
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      isPanning.value = false;
      if (scale.value === minScale) {
        translateX.value = 0;
        translateY.value = 0;
      }
    };

    const onImageLoad = () => {
      // Image loaded, could add loading state if needed
    };

    const close = () => {
      show.value = false;
    };

    const handleDelete = () => {
      // Emit delete event with current image index
      emit("delete", currentIndex.value);

      // If multiple images remain, navigate to the next/previous image
      if (hasMultipleImages.value && props.images.length > 1) {
        // If we're deleting the last image, go to previous
        if (currentIndex.value >= props.images.length - 1) {
          currentIndex.value = Math.max(0, currentIndex.value - 1);
        }
        // Otherwise stay on the same index (which will show the next image)
      } else {
        // If this was the last image, close the lightbox
        close();
      }
    };

    // Add global mouse listeners for panning and keyboard for navigation
    onMounted(() => {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("keydown", handleKeyDown);
    });

    onUnmounted(() => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keydown", handleKeyDown);
    });

    return {
      show,
      containerRef,
      imageRef,
      scale,
      translateX,
      translateY,
      minScale,
      maxScale,
      isPanning,
      isMobile,
      imageStyle,
      resetZoom,
      zoomIn,
      zoomOut,
      handleWheel,
      handleDoubleClick,
      handleMouseDown,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      onImageLoad,
      close,
      // Multiple images navigation
      currentIndex,
      currentImageUrl,
      currentDownloadUrl,
      currentTitle,
      hasMultipleImages,
      canGoPrev,
      canGoNext,
      goToPrev,
      goToNext,
      handleDelete,
    };
  },
};
</script>

<style scoped>
.lightbox-card {
  background-color: #000;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.lightbox-toolbar {
  flex-shrink: 0;
}

.lightbox-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: #000;
  cursor: default;
  padding: 0;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  transform-origin: center;
}

.zoom-hint {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  z-index: 10;
  animation: fadeInOut 3s ease-in-out infinite;
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* Prevent text selection while panning */
.lightbox-container.panning {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
}

/* Navigation buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.nav-btn:hover {
  opacity: 1;
}

.nav-btn-left {
  left: 16px;
}

.nav-btn-right {
  right: 16px;
}

@media (max-width: 600px) {
  .nav-btn {
    opacity: 0.6;
  }

  .nav-btn-left {
    left: 8px;
  }

  .nav-btn-right {
    right: 8px;
  }
}
</style>
